import React from "react";

import { format, isSameDay, isWithinInterval } from "date-fns";

import { borderRadius, color, spacing } from "../../tokens";
import { Box } from "../Box/Box";
import { Button } from "../Button/Button";
import { TempRange } from "./types";

interface CalendarDayProps {
  day: Date;
  currentMonth: Date;
  mode: "single" | "range";
  tempSingle: Date | null;
  tempRange: TempRange;
  hoverDate: Date | null;
  onClick: (day: Date) => void;
  onMouseEnter: (day: Date) => void;
  onMouseLeave: () => void;
}

export const CalendarDay: React.FC<CalendarDayProps> = ({
  day,
  currentMonth,
  mode,
  tempSingle,
  tempRange,
  hoverDate,
  onClick,
  onMouseEnter,
  onMouseLeave,
}) => {
  const isCurrentMonth = day.getMonth() === currentMonth.getMonth();

  const isSelectedSingle =
    mode === "single" && tempSingle != null && isSameDay(day, tempSingle);

  const isRangeStart =
    mode === "range" &&
    tempRange.start != null &&
    isSameDay(day, tempRange.start);

  const isRangeEnd =
    mode === "range" && tempRange.end != null && isSameDay(day, tempRange.end);

  const isInRange =
    mode === "range" &&
    tempRange.start != null &&
    tempRange.end != null &&
    isWithinInterval(day, { start: tempRange.start, end: tempRange.end });

  // Preview range while hovering before end date is picked
  const previewEnd =
    mode === "range" &&
    tempRange.start != null &&
    tempRange.end == null &&
    hoverDate != null &&
    hoverDate > tempRange.start
      ? hoverDate
      : null;

  const isPreviewStart = previewEnd != null && isRangeStart;
  const isPreviewEnd = previewEnd != null && isSameDay(day, previewEnd);
  const isInPreviewRange =
    previewEnd != null &&
    tempRange.start != null &&
    isWithinInterval(day, { start: tempRange.start, end: previewEnd });

  const isHighlighted =
    isSelectedSingle || isRangeStart || isRangeEnd || isPreviewEnd;

  // Half-gradient backgrounds for range endpoints, solid fill for in-between days
  const hasFullRange = tempRange.start != null && tempRange.end != null;
  const rangeBg =
    hasFullRange && isRangeStart
      ? `linear-gradient(to right, transparent 50%, ${color.neutral[200]} 50%)`
      : hasFullRange && isRangeEnd
      ? `linear-gradient(to left, transparent 50%, ${color.neutral[200]} 50%)`
      : isInRange
      ? color.neutral[200]
      : isPreviewStart
      ? `linear-gradient(to right, transparent 50%, ${color.neutral[200]} 50%)`
      : isPreviewEnd
      ? `linear-gradient(to left, transparent 50%, ${color.neutral[200]} 50%)`
      : isInPreviewRange
      ? color.neutral[200]
      : "transparent";

  return (
    <Box sx={{ width: "14%", mb: spacing.xs, background: rangeBg }}>
      <Button
        onClick={() => onClick(day)}
        onMouseEnter={() => onMouseEnter(day)}
        onMouseLeave={onMouseLeave}
        disableRipple
        sx={{
          minWidth: "30px",
          height: "35px",
          width: "100%",
          p: 0,
          textDecoration: "none",
          borderRadius: borderRadius.full,
          color: isCurrentMonth
            ? isHighlighted
              ? color.basic.black
              : "text.primary"
            : "text.disabled",
          backgroundColor: isHighlighted ? color.neutral[300] : "transparent",
          transition: "none",
          "&:hover": {
            backgroundColor: color.neutral[300],
            borderRadius: borderRadius.full,
          },
        }}
        variant="text"
      >
        {format(day, "d")}
      </Button>
    </Box>
  );
};
